import { DynamicForm, FormConfig } from "@components/DynamicForm/DynamicForm";
import { useNotification } from "@hooks/useNotification";
import { useQuery } from "@hooks/useQuery";
import { useDocumentDetails } from "@modules/ECMS/MAGP/ApprovalManagement/Drilldown.Hooks";
import { decodeHTML } from "@utils/CommonUtils";
import { useEffect, useState } from "react";
import { FieldValues } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { ContentWrapper } from "~/Components";
import { useAuth } from "~/Hooks/useAuth";
import { corporateCMSApi, post, put } from "~/Services";
import { corporateHost } from "~/Utils";
import { useAwardsDocumentDetails } from "./Awards.hook";
import { fields, fieldsReady } from "./constants";

export const AddEditCorporateAwards = () => {
  const query = useQuery();
  const navigate = useNavigate();
  const { openToast } = useNotification();
  const { user } = useAuth();
  const [records, setRecords] = useState<any[]>([]);
  const [fieldData, setFieldData] = useState<any>();

  async function fetchData() {
    await fieldsReady;

    setFieldData(fields);
  }
  useEffect(() => {
    fetchData();
  }, []);

  const {
    data: editData,
    error,
    isLoading: drilldownLoading,
    isError,
    refetch: refetchEditData,
  } = useAwardsDocumentDetails(query.get("editId") || "");

  const {
    data: dataById,
    isLoading: isLoadingData,
    refetch: refetchDocument,
  } = useDocumentDetails(query?.get("id") || "");

  useEffect(() => {
    if (query.get("id")) {
      refetchDocument();
    }
    if (query.get("editId")) {
      refetchEditData();
    }
  }, [query.get("editId"), query.get("id")]);
  const getRecords = () => {
    let url = `/api/get-awards/all-fields`;

    corporateCMSApi
      .get(url)
      .then((res: any) => {
        if (res && res.data) {
          let envelopes = res.data;
          setRecords(envelopes);
        }
      })
      .catch((err: any) => {
        setRecords([]);
      });
  };
  useEffect(() => {
    getRecords();
  }, []);

  const AwardsFormConfig: FormConfig = {
    initialValues: {
      title: dataById ? decodeHTML(dataById.payload.title) : editData ? decodeHTML(editData?.title) : "",
      field_awards_title_ms: dataById
        ? decodeHTML(dataById.payload.field_awards_title_ms)
        : editData
          ? decodeHTML(editData.field_awards_title_ms)
          : "",
      field_awards_zh: dataById
        ? decodeHTML(dataById.payload.field_awards_zh)
        : editData
          ? decodeHTML(editData?.field_awards_zh)
          : "",
      field_awards_page_name_en: dataById
        ? decodeHTML(dataById.payload.field_awards_page_name_en)
        : editData
          ? decodeHTML(editData?.field_awards_page_name_en)
          : "",
      field_awards_page_ms: dataById
        ? decodeHTML(dataById.payload.field_awards_page_ms)
        : editData
          ? decodeHTML(editData.field_awards_page_ms)
          : "",
      field_awards_page_zh: dataById
        ? decodeHTML(dataById.payload.field_awards_page_zh)
        : editData
          ? decodeHTML(editData?.field_awards_page_zh)
          : "",
      year: dataById
        ? new Date(decodeHTML(dataById.payload.year)).getFullYear().toString()
        : editData
          ? decodeHTML(editData?.year)
          : "",
      field_awards_description_en: dataById
        ? decodeHTML(dataById.payload.field_awards_description_en)
        : editData
          ? decodeHTML(editData?.field_awards_description_en)
          : "",
      field_awards_description_ms: dataById
        ? decodeHTML(dataById.payload.field_awards_description_ms)
        : editData
          ? decodeHTML(editData?.field_awards_description_ms)
          : "",
      field_awards_description_zh: dataById
        ? decodeHTML(dataById.payload.field_awards_description_zh)
        : editData
          ? decodeHTML(editData?.field_awards_description_zh)
          : "",
      sequence: dataById ? decodeHTML(dataById.payload.sequence) : editData ? decodeHTML(editData?.sequence) : "",
      published: dataById
        ? decodeHTML(dataById.payload?.published)
        : editData
          ? decodeHTML(editData?.published)
          : false,
      image: dataById?.payload
        ? decodeHTML([
            {
              uid: "-1",
              name: "image.png",
              status: "done",
              url: dataById?.payload?.image,
            },
          ])
        : decodeHTML([
            {
              uid: "-1",
              name: "image.png",
              status: "done",
              url: editData?.image,
            },
          ]),
    },
    fields: fieldData?.awardsPageContent,
  };

  const onSubmit = async (
    data: FieldValues,
    apiUrl: string,
    action: string,
    method: string,
    status: "DRAFT" | "PENDING",
    reviewNotes?: string
  ) => {
    let imageKeys: any = ["image", "field_awards_image"];
    for (const key of imageKeys) {
      try {
        let field = data[key];
        if (field[0].originFileObj) {
          const formData = new FormData();
          formData.append("upload-file", field[0].originFileObj);
          formData.append("application", "CORPORATE");
          const res: any = await post("management/upload-file", formData);

          if (res) {
            data[key] = res.response.data;
          }
        } else {
          data[key] = data[key][0].url;
        }
      } catch (error) {}
    }
    let numberKeys: any = ["year", "sequence", "field_awards_sequence", "field_awards_year"];

    for (const key of numberKeys) {
      try {
        let field = data[key];
        if (field !== undefined && field !== null && field !== "") {
          data[key] = Number(field);
        }
      } catch (error) {
        console.error(`Error converting ${key} to number:`, error);
      }
    }

    const payload = {
      action: action,
      method: method,
      module: "Awards",
      subModule: "awards_awardsPageContent",
      type: "DEDICATED",
      application: "MAHB_CORPORATE",
      reviewNotes: reviewNotes,
      moduleKey: "MAHB_CORPORATE_Awards",
      appList: [
        {
          appName: "MAHB_CORPORATE",
          apiUrl: apiUrl,
        },
      ],
      payload: {
        ...(action === "UPDATE" && { id: editData?.id || dataById?.payload?.id }),
        ...data,
        year: `${data?.year}-01-01`,
        field_awards_page_name_en: "awards",
        field_awards_page_ms: "",
        field_awards_page_zh: "",
      },
      status: status,
    };
    if (query.get("id")) {
      put(`/management/update-document?approvalId=${query.get("id")}`, payload)
        .then((response: any) => {
          openToast({
            content: status == "PENDING" ? "Data Sent for Review" : "Data Saved Successfully",
            type: "success",
          });
          if (response.status === "OK" && status == "PENDING") {
            navigate("/dedicated/status-information?activeTab=pending");
          } else {
            navigate("/dedicated/status-information?activeTab=draft");
          }
        })
        .catch((err: any) => {
          openToast({
            content: "Failed to save data",
            type: "error",
          });
        });
    } else {
      post("/management/publish", payload)
        .then((response: any) => {
          openToast({
            content: status == "DRAFT" ? "Data saved successfully" : "Data Sent for Review",
            type: "success",
          });
          if (response.status === "OK" && status == "PENDING") {
            navigate("/dedicated/status-information?activeTab=pending");
          } else {
            navigate("/dedicated/status-information?activeTab=draft");
          }
          // refetchDocument();
        })
        .catch((err: any) => {
          openToast({
            content: "Failed to save data",
            type: "error",
          });
        });
    }
  };

  return (
    <ContentWrapper
      // loading={isLoadingData}
      title={`${query.get("id") || query.get("editId") ? "Edit" : "Add New"} Award`}
      showBack
    >
      <DynamicForm
        isLoading={isLoadingData || drilldownLoading}
        config={AwardsFormConfig}
        onSubmit={(data, buttonValue, reviewNotes) =>
          onSubmit(
            data,
            query.get("editId") || query.get("type") === "update"
              ? `${corporateHost}/api/awards-view/${editData?.id || dataById?.payload?.id}`
              : `${corporateHost}/api/create/awards-view`,
            query.get("editId") || query.get("type") === "update" ? "UPDATE" : "CREATE",
            query.get("editId") || query.get("type") === "update" ? "PATCH" : "POST",
            buttonValue === "save" ? "DRAFT" : "PENDING",
            reviewNotes
          )
        }
        defaultReviewNotes="Kindly review this Award"
      />
    </ContentWrapper>
  );
};
